/**
 * ADOS-Informed Coding — Phase 2 of the 4-phase OBS appointment.
 *
 * Clinicians score the ADOS-2 Module 4 items based on the observation session.
 * There are no within-phase sub-steps, so the primary footer CTAs are the
 * phase jumps themselves (Back → Task Administration, Next → Criterion Mapping),
 * matching how a terminal History section promotes "Go to SSI" to a full button.
 *
 * LLM regeneration (v2 tagged-notes redesign only)
 * ------------------------------------------------
 * In v2 the ADOS item NOTES can be tagged to DSM criteria (or `self_reported`),
 * so they feed the Criteria and Self-Report paragraphs exactly like Task
 * Administration notes. This page therefore gained exit-regeneration wiring:
 *
 *   • Editing an ADOS note (text OR tags) marks the Criteria section dirty; a
 *     `self_reported` tag change additionally marks Self-Report dirty.
 *   • Editing an ADOS numeric CODE never dirties anything — codes are not an
 *     LLM input.
 *   • On any managed exit (Back / Next / breadcrumb / top nav) we flush writes
 *     and REGENERATE the affected sections — but only ones that were ALREADY
 *     generated. ADOS never performs a first-time generation; that is owned by
 *     Criterion Mapping → Review and the Review-page catch-up. So a clinician
 *     passing forward through ADOS on their first lap triggers nothing.
 *
 * v1 patients see no tag pickers and write no ADOS notes, so none of this fires
 * for them — the page behaves exactly as before.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../../../../components/Layout';
import Breadcrumb from '../../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../../components/autismModular/AppointmentChecklistControls';
import PreviewModeBanner from '../../../../components/PreviewModeBanner';
import ModularStepFooter from '../../../../components/autismModular/ModularStepFooter';
import AdosScoringPanel from '../components/AdosScoringPanel';
import { useDragAutoScroll } from '../../../../hooks/useDragAutoScroll';
import { useModularSectionExitRegeneration } from '../../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../../utils/TriggerGenerateDocumentSection';
import { getAutismOBSStepRoutes } from '../../../../utils/autismObsRouting';
import { selfReportedTaggingChanged } from '../../../../utils/obsSelfReportGeneration';
import { hasAnyObservationCriterionDecision } from '../../../../utils/observationCriteriaCompletion';
import useObservationStageData from '../hooks/useObservationStageData';

export default function AdosCoding() {
  const {
    patientId,
    tasks,
    navigate,
    flushStageWrites,
    isPreviewMode,
    canEdit,
    isObsV2,
    reportContent,
    selectedTaskIds,
    allTaskData,
    criteriaEvidence,
    adosData,
    handleAdosFieldChange,
    isAppointmentChecklistEnabled,
  } = useObservationStageData();

  const showChecklist = isAppointmentChecklistEnabled;

  const contentRef = useRef(null);
  useDragAutoScroll(contentRef);

  // ── Dirty tracking (v2 only) ────────────────────────────────────────────
  // `hasCriteriaChanges` drives both the shared navigation guard (so a dirty
  // breadcrumb/top-nav exit shows the leaving-section dialog) and the Criteria
  // regeneration. `selfReportDirtyRef` is a plain ref because Self-Report is
  // this page's SECOND section — the one-guard-per-page constraint means only
  // Criteria registers with the hook, and Self-Report rides a plain unmount net
  // (the same split used on Task Administration).
  const [hasCriteriaChanges, setHasCriteriaChanges] = useState(false);
  const selfReportDirtyRef = useRef(false);
  const selfReportRegenHandledRef = useRef(false);

  const criteriaAlreadyGenerated = Boolean(
    reportContent?.observation?.stageSectionsGenerated?.includes('Criteria'),
  );

  // Regenerate — never draft — from ADOS: only warranted when the section
  // already exists AND an ADOS note changed this visit.
  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: 'observation',
    sectionTitle: 'Criteria',
    hasChanges: hasCriteriaChanges,
    canEdit,
    reportContent,
    shouldRegenerate: hasCriteriaChanges && criteriaAlreadyGenerated,
    suppressInitialGeneration: true,
  });

  // Wrap the field writer to detect what changed. Only NOTE fields
  // (`ados_<id>_notes`) are LLM inputs; score fields are ignored.
  const handleAdosFieldChangeWrapped = useCallback((fieldKey, value) => {
    if (isObsV2 && typeof fieldKey === 'string' && fieldKey.endsWith('_notes')) {
      setHasCriteriaChanges(true);
      const prevNotes = adosData?.[fieldKey];
      if (selfReportedTaggingChanged(prevNotes, value)) {
        selfReportDirtyRef.current = true;
      }
    }
    handleAdosFieldChange(fieldKey, value);
  }, [handleAdosFieldChange, isObsV2, adosData]);

  // Shared exit routine for both managed phase jumps. Flushes once so the CF
  // reads settled data, then regenerates any already-generated section whose
  // inputs changed. Marks both sections handled so the unmount nets do not
  // double-fire.
  const flushAndRegenerateOnExit = useCallback(() => {
    markRegenHandled();
    const dataReady = flushStageWrites('observation');
    if (canEdit) {
      const generated = reportContent?.observation?.stageSectionsGenerated || [];
      if (
        hasCriteriaChanges
        && generated.includes('Criteria')
        && hasAnyObservationCriterionDecision(
          selectedTaskIds, tasks, allTaskData, criteriaEvidence,
        )
      ) {
        TriggerGenerateDocumentSection(
          patientId, 'Autism Behavioural Observation', 'Criteria', [], dataReady,
        );
      }
      if (selfReportDirtyRef.current && generated.includes('Self-Report')) {
        selfReportRegenHandledRef.current = true;
        TriggerGenerateDocumentSection(
          patientId, 'Autism Behavioural Observation', 'Self-Report', [], dataReady,
        );
      }
    }
    return dataReady;
  }, [
    markRegenHandled, flushStageWrites, canEdit, reportContent, hasCriteriaChanges,
    selectedTaskIds, tasks, allTaskData, criteriaEvidence, patientId,
  ]);

  // Unmanaged-exit net for Self-Report (breadcrumb, browser back). Criteria is
  // covered by the exit-regeneration hook; this plain effect mirrors the
  // Task-Administration pattern for the second section. A ref mirror keeps the
  // cleanup reading current values without re-running on every change.
  const selfReportExitRef = useRef(null);
  selfReportExitRef.current = () => {
    if (selfReportRegenHandledRef.current) return;
    if (!canEdit) return;
    if (!selfReportDirtyRef.current) return;
    const generated = reportContent?.observation?.stageSectionsGenerated || [];
    if (!generated.includes('Self-Report')) return;
    TriggerGenerateDocumentSection(
      patientId, 'Autism Behavioural Observation', 'Self-Report', [],
    );
  };
  useEffect(() => () => {
    if (selfReportExitRef.current) selfReportExitRef.current();
  }, []);

  const handleBackToTaskAdmin = useCallback(async () => {
    await flushAndRegenerateOnExit();
    navigate(getAutismOBSStepRoutes(patientId).taskAdministration);
  }, [flushAndRegenerateOnExit, navigate, patientId]);

  const handleGoToCriterionMapping = useCallback(async () => {
    await flushAndRegenerateOnExit();
    navigate(getAutismOBSStepRoutes(patientId).criterionMapping);
  }, [flushAndRegenerateOnExit, navigate, patientId]);

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Behavioural Observation'],
    ['ADOS-Informed Coding'],
  ];

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="observation" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          {isPreviewMode && <PreviewModeBanner />}
          <div ref={contentRef} className="flex-1 min-h-0 overflow-y-auto">
            <AdosScoringPanel
              adosData={adosData}
              onAdosFieldChange={handleAdosFieldChangeWrapped}
              readOnly={!canEdit}
              // v2 only: ADOS note tag pickers feed the tagged-notes pipeline.
              taggingEnabled={isObsV2}
            />
          </div>

          {/* No within-phase steps — both directions are phase jumps as full CTAs. */}
          <ModularStepFooter
            showBack
            backLabel="Task Administration"
            onBack={handleBackToTaskAdmin}
            showNext
            nextLabel="Go to Criterion Mapping"
            onNext={handleGoToCriterionMapping}
          />
        </div>
      </div>
      </div>
    </Layout>
  );
}
